import axios from "axios";
import { getEmailFromUserId } from "./utils/event-helpers";
import type { User, UserStatus } from "./stores/user/userSlice";
import type { ResourceBookmark, MentorBookmark } from "./shared/bookmarkTypes";
import type { FieldScore, MentorRecommendations } from "./shared/recommendationTypes";

// ============================================================================
// LTIK helpers
// ============================================================================

export const LTIK_LS_KEY = "LTIK_LS";

export function getLtiKeyFromStorages() {
  const searchParams = new URLSearchParams(window.location.search);
  const ltikFromSearchParams = searchParams.get("ltik");
  const ltikFromLocalStorage = localStorage.getItem(LTIK_LS_KEY);
  return ltikFromSearchParams ?? ltikFromLocalStorage;
}

export const getLtik = () => {
  const ltik = getLtiKeyFromStorages();
  if (!ltik) throw new Error("Missing lti key.");
  return ltik;
};

// ============================================================================
// URL helpers
// ============================================================================

export function addUrlParams(url: string, params: Record<string, string>) {
  const urlParams = new URLSearchParams(params);
  return url + "?" + urlParams.toString();
}

// ============================================================================
// API types
// ============================================================================

interface MentorRecommendationScoresResponse {
  subfieldScores: FieldScore[];
  pageFieldScores: FieldScore[];
}

// ============================================================================
// GraphQL queries
// ============================================================================

const verifyAccessCodeQuery = `
query ValidateAccessCode($orgAccessCode: String!, $orgId: String!) {
    validateAccessCode(orgAccessCode: $orgAccessCode, orgId: $orgId)
}`;

// ============================================================================
// Axios return types
// ============================================================================

interface GetResourceBookmarksResponse {
  bookmarks: ResourceBookmark[];
}

interface PostResourceBookmarksResponse {
  bookmark: ResourceBookmark;
}

interface GetMentorBookmarksResponse {
  bookmarks: MentorBookmark[];
}

interface PostMentorBookmarksResponse {
  bookmark: MentorBookmark;
}

interface GetMentorRecommendationsResponse {
  mentorRecommendations: MentorRecommendations | null;
}

interface PutMentorRecommendationsResponse {
  mentorRecommendations: MentorRecommendations;
}

interface GetUserResponse {
  user: User | null;
}

interface PatchUserStatusResponse {
  status: UserStatus;
}

interface VerifyAccessCodeResponse {
  data: {
    validateAccessCode: unknown;
  };
}

// ============================================================================
// API calls
// ============================================================================

const userRouteEndpoint = import.meta.env.VITE_USER_ROUTE;

export async function postGrade(grade: number) {
  const litjsEndpint = import.meta.env.VITE_LITJS_ENDPOINT;
  try {
    const body = {
      grade: grade,
    };
    await axios.post(litjsEndpint + "/grade", body, {
      withCredentials: true,
      headers: {
        Authorization: "Bearer " + getLtik(),
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Error posting grade:", error);
  }
}

export async function mentorSessionRecommendations(
  userId: string,
  sessionId: string,
  numResults: number,
  timestampStart: string,
  timestampEnd?: string,
): Promise<MentorRecommendationScoresResponse> {
  const endpoint = import.meta.env.VITE_MENTOR_LRS_RECOMMENDATIONS_ENDPOINT;

  const url = addUrlParams(endpoint, {
    name: userId,
    sessionId: sessionId,
    mbox: `mailto:${getEmailFromUserId(userId)}`,
    timestampStart: timestampStart,
    ...(timestampEnd && { timestampEnd: timestampEnd }),
    numResults: numResults.toString(),
  });
  try {
    const response = await axios.get(url);
    return response.data as MentorRecommendationScoresResponse;
  } catch (error) {
    console.error("Error getting mentor session recommendations:", error);
    throw error;
  }
}

export async function getResourceBookmarks(
  userId: string,
): Promise<ResourceBookmark[]> {
  const response = await axios.get<GetResourceBookmarksResponse>(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/resource-bookmarks`,
  );

  return response.data.bookmarks;
}

export async function postResourceBookmark(
  userId: string,
  resourceId: string,
): Promise<ResourceBookmark> {
  const response = await axios.post<PostResourceBookmarksResponse>(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/resource-bookmarks`,
    {
      resourceId,
    },
  );

  return response.data.bookmark;
}

export async function deleteResourceBookmark(
  userId: string,
  resourceId: string,
): Promise<void> {
  await axios.delete(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/resource-bookmarks/${encodeURIComponent(resourceId)}`,
  );
}

export async function getMentorBookmarks(
  userId: string,
): Promise<MentorBookmark[]> {
  const response = await axios.get<GetMentorBookmarksResponse>(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/mentor-bookmarks`,
  );

  return response.data.bookmarks;
}

export async function postMentorBookmark(
  userId: string,
  mentorId: string,
): Promise<MentorBookmark> {
  const response = await axios.post<PostMentorBookmarksResponse>(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/mentor-bookmarks`,
    {
      mentorId,
    },
  );

  return response.data.bookmark;
}

export async function deleteMentorBookmark(
  userId: string,
  mentorId: string,
): Promise<void> {
  await axios.delete(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/mentor-bookmarks/${encodeURIComponent(mentorId)}`,
  );
}

export async function getMentorRecommendations(
  userId: string,
): Promise<MentorRecommendations | null> {
  const response = await axios.get<GetMentorRecommendationsResponse>(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/mentor-recommendations`,
  );

  return response.data.mentorRecommendations;
}

export async function putMentorRecommendations(
  userId: string,
  recommendations: MentorRecommendations,
): Promise<MentorRecommendations> {
  const response = await axios.put<PutMentorRecommendationsResponse>(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/mentor-recommendations`,
    recommendations,
  );

  return response.data.mentorRecommendations;
}

export async function deleteMentorRecommendations(
  userId: string,
): Promise<void> {
  await axios.delete(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/mentor-recommendations`,
  );
}

export async function getUserApi(userId: string): Promise<User | null> {
  const response = await axios.get<GetUserResponse>(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}`,
  );

  return response.data.user ?? null;
}

export async function patchUserStatusApi(
  userId: string,
  statusUpdates: Partial<UserStatus>,
): Promise<UserStatus> {
  const response = await axios.patch<PatchUserStatusResponse>(
    `${userRouteEndpoint}/${encodeURIComponent(userId)}/status`,
    statusUpdates,
  );

  return response.data.status;
}

export async function verifyAccessCode(
  orgAccessCode: string,
): Promise<boolean> {
  const endpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT;
  const orgId = import.meta.env.VITE_PATHWAYS_ORG_ID;

  try {
    const res = await axios.post<VerifyAccessCodeResponse>(endpoint, {
      query: verifyAccessCodeQuery,
      variables: {
        orgId,
        orgAccessCode,
      },
    });

    return res.data.data.validateAccessCode as boolean;
  } catch (error) {
    console.error("Error verifying access code:", error);
    throw new Error("Access code verification failed.", { cause: error });
  }
}
